import { PlacementXAnchor, TextAlignment } from "building-types/annotation/annotationTypesEnum";
import type { ProfileExtrusionDescriptor } from "building-types/profiles/profileTypes";
import { ElevationNotePlacementType } from "building-types/settings/settingsTypesEnum";
import type { ViewNoteSettings } from "building-types/settings/viewNoteSettings";
import type { ElevationNote } from "building-types/viewNotes/elevationNoteTypes";
import type { ElevationAnnotationParams } from "building-types/views/elevationAnnotationTypes";
import {
  defaultElevationAnnotationParams,
  ElevationNoteEntityDebugMode,
} from "building-types/views/elevationAnnotationTypes";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew, sheetTextGeometryNew } from "building-types/views/sheetGeometryTypes";
import { DefaultViewEntityCollisionWeight, EntityAnnotationEnabledTypes } from "building-types/views/viewEntityNotes";
import type { ViewEntityTable } from "building-types/views/viewEntityTable";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import { ONE_INCH } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import {
  assert,
  assertIsNotNull,
  assertIsNotNullOrUndefined,
  assertIsNumber,
  ensureDefined,
} from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { randomStableColorHex } from "common/utils/color";
import hashString from "common/utils/hashString";
import init2DArray from "common/utils/init2DArray";
import { degreeToRadian } from "common/utils/math";
import {
  numAdd,
  numDiv,
  percentNew,
  sheetInchesNew,
  sheetPointsNew,
  sheetPointsToInches,
} from "common/utils/nominalNumbers";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import {
  supplyGeometryListTagIfMissing,
  supplyGeometryTagIfMissing,
  type PlanGeometryBuilderWithCollider,
} from "doc/plans/planGeometrySource";
import type { SheetGeometryStyler, TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { sheetGeometryStylePass } from "doc/plans/primitives/primitiveStyles";
import { planTextGeometryAABB } from "doc/plans/primitives/textPrimitive";
import { getDescriptorEntityAnnotationSetting } from "doc/utils/viewEntityTools";
import { aabb2ByPolyLine2s, aabb2ContainsVec2, aabb2Copy, aabb2Move, aabb2Overlaps } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { angleClean } from "geom/angle";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { offsetClosedPolyLine2 } from "geom/polyclip/offsetClosedPolyLine2";
import { PolygonRoundingMode, polygonUnion } from "geom/polyclip/polyClip";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2FromAABB2,
  polyLine2SetCCW,
  polyLine2Simplify,
  polyLine2TransformCopy,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { rect2ByBottomLeftWidthHeight, rect2ByCenterWidthHeight } from "geom/rect2";
import { vec2AngleBetweenCW, vec2DistSquared, vec2Sub, type Vec2 } from "geom/vec2";
import { generateArrowheadGeometry } from "lib/house/annotation/arrowheadGeometry";
import { generateElevNoteSheetGeometries } from "lib/house/annotation/tags/elevNote";
import {
  generateElevNoteWithLongLeaderLabel,
  generateElevNoteWithLongLeaderSheetGeometry,
} from "lib/house/annotation/tags/elevNoteWithLongLeader";
import type { EntityPolyLine } from "lib/house/plans/perspectiveEntities";
import { type CollisionShapeType, elevationSheetGeometryAABB2, type CollisionSystem2 } from "./collision2";
import { ElevationAnnotationPlacementError } from "./PDFUserFacingError";

const PolyLineUnionReasonableLimit = 50;
const PolyLineUnionChunkSize = 5;
const MinimumBBMinDimForTagging = 2;
const MinimumBBMaxDimForTagging = 3;
const MinimumBBArea = 10;

const PERIMETER_PADDING = ONE_INCH;

const ON_PERIMETER_SCALE_FACTOR = 0.25;

const ASSIGNMENT_DEBUG_LOGGING = false;

const totalAreaByAABBViewEntityType: { [K in ViewEntityType]?: boolean } = {
  [ViewEntityType.PROP]: true,
};

export type EntityAnnotationInfo = AutoEntityAnnotationInfo | ManualEntityAnnotationInfo;

export interface ManualEntityAnnotationInfo {
  readonly type: "MANUAL";
  readonly elevationNote: ElevationNote;
  readonly rx: number;
  readonly ry: number;
  readonly screenX: number;
  readonly screenY: number;
}

export interface AutoEntityAnnotationInfo {
  readonly type: "AUTO";
  /* projected boundary of object to be annotated */
  readonly poly: PolyLine2;
  readonly text: string;
  readonly key?: number; // identifier for the entity when we have multiple candidates
}

export interface BoundingBoxPlacementReproAutoNote {
  readonly key: number;
  readonly text: string;
  readonly poly: number[];
}

export interface BoundingBoxPlacementReproCollisionItem {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly weight: number;
  readonly blocksLeaderLines?: boolean;
  readonly shapeType?: CollisionShapeType;
  readonly polyline?: number[];
}

export interface BoundingBoxPlacementReproStyledLabel {
  readonly key: number;
  readonly side: "LEFT" | "RIGHT";
  readonly text: string;
  readonly fontSize?: number;
  readonly font?: string;
  readonly boxWidth?: number;
  readonly alignment?: TextAlignment;
  readonly placementXAnchor?: PlacementXAnchor;
  readonly rotation?: number;
}

export interface BoundingBoxPlacementReproData {
  readonly worldBB: AABB2;
  readonly scaleFactor: number;
  readonly textScalingFactor: number;
  readonly showElevationNoteEntities: ElevationNoteEntityDebugMode;
  readonly elevationAnnotationParams: ElevationAnnotationParams;
  readonly computedSpacing: {
    readonly boxToElbowX: number;
    readonly boxToLabelX: number;
    readonly textWidth: number;
  };
  readonly autoNotes: BoundingBoxPlacementReproAutoNote[];
  readonly collisionItems: BoundingBoxPlacementReproCollisionItem[];
  readonly styledLabels: BoundingBoxPlacementReproStyledLabel[];
  readonly errorMessage?: string;
}

interface AreaEntityAnnotationInfo extends AutoEntityAnnotationInfo {
  readonly area: number;
}

type EntityTypesDebugInfo = {
  [key in ViewEntityType]?: { polylines: PolyLine2[] };
};

type EntitiesAnnotationTable = { [key: number]: AreaEntityAnnotationInfo[] };
type EntityAnnotationInfoTable = { [key: number]: AreaEntityAnnotationInfo };

function renderEntityTypeDebugGeometry(
  debugInfo: EntityTypesDebugInfo,
  primitivesOut: PlanGeometryBuilderWithCollider,
): void {
  const legendYOffset = 0.2;
  const spacing = 0.05;

  const legendXPosition = primitivesOut.bb.minX + 5.0;
  const legendYPosition = primitivesOut.bb.minY + 2.0;
  let legendIndex = 0;
  for (const entityType of objectTypedKeys(debugInfo).sort()) {
    // Use a different color for stroke and fill to make them easier to see
    const strokeColor = randomStableColorHex(hashString(entityType));
    const fillColor = randomStableColorHex(hashString(entityType) + 1);
    const { polylines } = ensureDefined(debugInfo[entityType]);

    // Inset rectangles by a small amount to make them easier to see
    primitivesOut.addWorldSpaceGeometry(
      sheetPolylineGeometryNew({
        paths: polylines.map((x) => polyLine2Copy(x)),
        linewidth: sheetPointsNew(0.4),
        fill: true,
        fillColor,
        strokeColor,
        fillOpacity: percentNew(0.5),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );

    // Make a small legend for the entity type counting the number of entity types for the y offset,
    // showing a polyline for the color and text for the entity type

    const i = legendIndex;
    legendIndex++;
    const path = rect2ByBottomLeftWidthHeight(
      0.1 + legendXPosition,
      i * legendYOffset + legendYPosition,
      0.7,
      spacing * 2,
    );
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: [path],
        linewidth: sheetPointsNew(0.4),
        strokeColor,
        fillColor,
        fillOpacity: percentNew(0.5),
        fill: true,
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetTextGeometryNew({
        text: entityType,
        x: legendXPosition,
        y: i * legendYOffset + legendYPosition + spacing,
        placementXAnchor: PlacementXAnchor.RIGHT,
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }
}

function weldAndUnionPolylines(polyLines: PolyLine2[]): PolyLine2[] {
  const seen: Vec2[] = [];

  const MAX_DIST_SQUARED = 0.005;
  for (const poly of polyLines) {
    for (let vertexIndex = 0; vertexIndex < poly.length; vertexIndex += 2) {
      const vec = [poly[vertexIndex], poly[vertexIndex + 1]];

      let found = false;
      for (const priorVec of seen) {
        if (vec2DistSquared(vec[0], vec[1], priorVec[0], priorVec[1]) < MAX_DIST_SQUARED) {
          poly[vertexIndex] = priorVec[0];
          poly[vertexIndex + 1] = priorVec[1];
          found = true;
          break;
        }
      }

      if (!found) {
        seen.push(vec);
      }
    }
  }

  // union the polygons
  const unionedPolyLines1 = polygonUnion({
    pls: polyLines.map((x) => [x]),
    filterAreaTol: -1,
    roundingMode: PolygonRoundingMode.SNAP_ROUND,
    mergeCoincidentVertexTol: 1e-3,
  }).map((x) => x[0]);

  return unionedPolyLines1;
}

function preprocessPolyLines(
  entityId: string,
  polyLines: PolyLine2[],
  polylineUnionLimit: number,
  primitivesOut: PlanGeometryBuilderWithCollider,
  collisionSystem: CollisionSystem2,
  entityTable: ViewEntityTable,
): PolyLine2[] {
  const preOffsetAndUnion: PolyLine2[] = [];
  if (polyLines.length > polylineUnionLimit) {
    const chunkPolys = splitIntoChunks(polyLines, PolyLineUnionChunkSize);
    for (const chunk of chunkPolys) {
      if (!chunk || chunk.length === 0) {
        continue;
      }
      const mergedChunk = polygonUnion({
        pls: chunk.map((x) => [x]),
        roundingMode: PolygonRoundingMode.SNAP_ROUND,
      }).map((x) => x[0]);
      if (!mergedChunk || mergedChunk.length === 0) {
        continue;
      }
      preOffsetAndUnion.push(...mergedChunk);
    }
  } else {
    preOffsetAndUnion.push(...polyLines);
  }

  const mergedPolyLines = polygonUnion({
    pls: preOffsetAndUnion.map((x) => [x]),
    roundingMode: PolygonRoundingMode.SNAP_ROUND,
  }).map((x) => x[0]);
  if (!polyLines || polyLines.length === 0) {
    return [];
  }

  const pdfMat = primitivesOut.modelSpaceToPaperSpaceTransform;
  for (const polyline of polyLines.map((x) => polyLine2TransformCopy(x, pdfMat))) {
    collisionSystem.insertPolyLine2(
      polyline,
      DefaultViewEntityCollisionWeight[entityTable.getEntityType(entityId)] ?? 2,
    );
  }

  return mergedPolyLines;
}

type CollectEntityAnnotationsInViewParams = {
  collisionSystem: CollisionSystem2;
  entityTable: ViewEntityTable;
  primitivesOut: PlanGeometryBuilderWithCollider;
  settings: ViewNoteSettings;
  entityPolylines: EntityPolyLine[];
  elevationNotePlacementType: ElevationNotePlacementType;
  polylineUnionLimit?: number;
  showElevationNoteEntities: ElevationNoteEntityDebugMode;
};

export function collectEntityAnnotationsInView({
  collisionSystem,
  entityTable,
  primitivesOut,
  settings,
  entityPolylines,
  elevationNotePlacementType,
  polylineUnionLimit = PolyLineUnionReasonableLimit,
  showElevationNoteEntities,
}: CollectEntityAnnotationsInViewParams): AutoEntityAnnotationInfo[] {
  const aggregatePolyLines = aggregatePolyLinesPerEntity(entityPolylines);
  const entityAnnotationInfos: AutoEntityAnnotationInfo[] = [];

  const selectedEntityAnnotations: EntitiesAnnotationTable = {};

  const debugInfo: EntityTypesDebugInfo = {};

  for (const entityId in aggregatePolyLines) {
    let polylines = aggregatePolyLines[entityId];
    if (!polylines || polylines.length === 0) {
      continue;
    }

    if (elevationNotePlacementType !== ElevationNotePlacementType.BOUNDING_BOX) {
      polylines = preprocessPolyLines(
        entityId,
        polylines,
        polylineUnionLimit,
        primitivesOut,
        collisionSystem,
        entityTable,
      );
    }

    const entityDescriptor = entityTable.getEntityDescriptor(entityId);
    if (entityDescriptor && entityDescriptor.entityType) {
      const entityType = entityDescriptor.entityType;
      if (!(entityType in debugInfo)) {
        debugInfo[entityType] = {
          polylines: [],
        };
      }
      assertIsNotNullOrUndefined(debugInfo[entityType]);
      debugInfo[entityType].polylines.push(...polylines.map((x) => polyLine2Copy(x)));
    }

    filterEntityCandidatesToAnnotate({
      entityId,
      polylines,
      entityTable,
      entityAnnotationInfos,
      selectedEntityAnnotations,
      settings,
      minimumArea: MinimumBBArea,
      minimumBBMinDimForTagging: MinimumBBMinDimForTagging,
      minimumBBMaxDimForTagging: MinimumBBMaxDimForTagging,
      elevationNotePlacementType,
    });
  }

  function areaReducer(a: AreaEntityAnnotationInfo, b: AreaEntityAnnotationInfo): AreaEntityAnnotationInfo {
    return a.area !== undefined && b.area !== undefined && a.area > b.area ? a : b;
  }

  // get entities with largest area polylines
  const largestAreaOfEntityAnnotations: EntityAnnotationInfoTable = {};
  for (const key in selectedEntityAnnotations) {
    largestAreaOfEntityAnnotations[key] = selectedEntityAnnotations[key].reduce(areaReducer);
  }

  switch (elevationNotePlacementType) {
    case ElevationNotePlacementType.IN_PLACE: {
      // simply use the entity with the largest polyline area
      for (const key in largestAreaOfEntityAnnotations) {
        entityAnnotationInfos.push(largestAreaOfEntityAnnotations[key]);
      }
      break;
    }
    case ElevationNotePlacementType.ON_PERIMETER: {
      // use the furthest left entity that is at least a certain percentage of the largest polyline area

      for (const key in selectedEntityAnnotations) {
        const annotationInfos = selectedEntityAnnotations[key];
        let leastX = Infinity;
        let leftMost: AreaEntityAnnotationInfo | undefined = undefined;
        annotationInfos.forEach((a) => {
          const centroid = polyLine2Centroid(a.poly);
          assertIsNotNull(centroid);
          const centroidX = centroid[0];
          const largest = largestAreaOfEntityAnnotations[key].area;
          if (centroidX < leastX && a.area > largest * ON_PERIMETER_SCALE_FACTOR) {
            leftMost = a;
            leastX = centroidX;
          }
        });
        if (leftMost) {
          entityAnnotationInfos.push(leftMost);
        }
      }

      break;
    }
    case ElevationNotePlacementType.BOUNDING_BOX: {
      // We weld and union polylines with the same selected entity key
      const values = Object.values(selectedEntityAnnotations);
      for (let key = 0; key < values.length; key++) {
        const annotationInfos = values[key];
        if (annotationInfos.length === 0) {
          continue;
        }
        let mergedPolyLines = weldAndUnionPolylines(annotationInfos.map((a) => a.poly));
        if (mergedPolyLines.length === 0) {
          // Something went wrong with the welding and unioning. Just use the original set.
          mergedPolyLines = annotationInfos.map((a) => a.poly);
        }
        for (const poly of mergedPolyLines) {
          entityAnnotationInfos.push({ type: "AUTO", poly, text: annotationInfos[0].text, key });
        }
      }
      break;
    }
    default:
      assertUnreachable(elevationNotePlacementType);
  }

  if (showElevationNoteEntities === ElevationNoteEntityDebugMode.ALL) {
    renderEntityTypeDebugGeometry(debugInfo, primitivesOut);
  } else if (
    showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED ||
    showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX
  ) {
    const taggedDebugInfo: EntityTypesDebugInfo = {};
    for (const annotationInfo of entityAnnotationInfos) {
      if (!(annotationInfo.text in taggedDebugInfo)) {
        taggedDebugInfo[annotationInfo.text] = { polylines: [] };
      }
      taggedDebugInfo[annotationInfo.text].polylines.push(annotationInfo.poly);
    }
    renderEntityTypeDebugGeometry(taggedDebugInfo, primitivesOut);
  }

  return entityAnnotationInfos;
}

function splitIntoChunks(arr: PolyLine2[], chunkSize: number): PolyLine2[][] {
  const chunks: PolyLine2[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    chunks.push(chunk);
  }
  return chunks;
}

function aggregatePolyLinesPerEntity(entityPolylines: EntityPolyLine[]) {
  const aggregateEntityPolylines: { [entityId: string]: PolyLine2[] } = {};
  for (const { entityId, polylines } of entityPolylines) {
    if (!entityId || polylines.length === 0) {
      continue;
    }

    const polylineList = aggregateEntityPolylines[entityId] || [];
    for (const p of polylines) {
      if (p.length === 0) {
        continue;
      }
      polylineList.push(polyLine2SetCCW(p));
    }
    aggregateEntityPolylines[entityId] = polylineList;
  }
  return aggregateEntityPolylines;
}

export function legacyPlaceEntityAnnotationsOnPerimeter(
  entityAnnotationInfo: EntityAnnotationInfo[],
  category: SheetGeometryCategory,
  primitivesOut: PlanGeometryBuilderWithCollider,
  textScalingFactor: number,
  sheetGeometryStyler?: SheetGeometryStyler,
): void {
  enum NoteBoundingBoxSide {
    TOP,
    BOTTOM,
    LEFT,
    RIGHT,
  }

  // Measurement only. The styler inflates font sizes by the viewport/elevation scale ratio for
  // bounding-box math, so its output must never be emitted: geometry outranks the stylesheet, and
  // baking a measurement size back onto the label is what made these notes unstyleable to begin
  // with. `placeEntityAnnotationsOnBoxPerimeter` splits the two the same way.
  const measurementLabel = (text: string): TaggedPlanGeometry => {
    const geometry = supplyGeometryTagIfMissing(
      generateElevNoteWithLongLeaderLabel({ text }),
      SheetGeometryCategory.MATERIAL_TAGS,
    );
    return sheetGeometryStyler ? sheetGeometryStyler([geometry], category)[0] : geometry;
  };

  // for each annotation, determine its position and the closest edge of the drawing bounding box
  const noteBoundingBoxSides = entityAnnotationInfo
    .map((info) => {
      // compute the distance to each side from the poly centroid
      let centroid: Vec2 | undefined;

      if (info.type === "AUTO") {
        centroid = polyLine2Centroid(info.poly)?.slice();
      } else {
        centroid = [info.screenX, info.screenY];
      }

      if (!centroid) {
        return null;
      }

      // the following is set up to handle drawing sides other than LEFT
      const noteSideDistances: [NoteBoundingBoxSide, number][] = [[NoteBoundingBoxSide.LEFT, 0]];

      // get the minimum distance side
      let min: [NoteBoundingBoxSide, number] | null = null;
      for (const pair of noteSideDistances) {
        if (min === null) {
          min = pair;
          continue;
        }

        if (min[1] > pair[1]) {
          min = pair;
        }
      }

      if (!min) {
        return null;
      }

      return [info, min[0], centroid];
    })
    .filter((x) => x !== null) as [EntityAnnotationInfo, NoteBoundingBoxSide, Vec2][];

  // sort annotations along the appropriate axis
  const leftNotes = noteBoundingBoxSides
    .filter((x) => x[1] === NoteBoundingBoxSide.LEFT)
    .sort((a, b) => a[2][1] - b[2][1]);

  // place notes in sorted order while attempting to prevent overlaps
  function placeNotesOnPerimeter(tuples: [EntityAnnotationInfo, NoteBoundingBoxSide, Vec2][]) {
    if (tuples.length === 0) {
      return;
    }

    const texts = tuples.map((tuple) => {
      const [note] = tuple;

      let text: string;
      if (note.type === "AUTO") {
        text = note.text;
      } else {
        text = note.elevationNote.elevationNote.text;
      }

      return text;
    });

    const textMeasurements = texts.map((text) => {
      return elevationSheetGeometryAABB2(measurementLabel(text), textScalingFactor);
    });

    const targets: Vec2[] = tuples.map((tuple) => {
      return tuple[2];
    });

    // TODO different placement strategies

    const adjustedLabelPositions: number[] = [];
    const textAlignments: TextAlignment[] = [];

    let currentEnd: number | undefined = undefined;

    const PADDING = sheetPointsToInches(sheetPointsNew(12));

    tuples.forEach((tuple, i) => {
      switch (tuple[1]) {
        case NoteBoundingBoxSide.TOP:
        case NoteBoundingBoxSide.BOTTOM: {
          const m = textMeasurements[i];
          assertIsNotNullOrUndefined(m);
          const [x] = targets[i];
          const labelMeasure = aabb2Width(m) / 2 + PADDING;
          const labelEnd = x - labelMeasure;

          if (currentEnd === undefined || labelEnd > currentEnd) {
            currentEnd = x;
            adjustedLabelPositions.push(currentEnd);
            textAlignments.push(TextAlignment.RIGHT);
          } else {
            if (x - PADDING > currentEnd) {
              adjustedLabelPositions.push(x);
              currentEnd = x + labelMeasure;
              textAlignments.push(TextAlignment.LEFT);
            } else {
              currentEnd += PADDING;
              adjustedLabelPositions.push(currentEnd);
              currentEnd += labelMeasure;
              textAlignments.push(TextAlignment.LEFT);
            }
          }

          break;
        }
        case NoteBoundingBoxSide.LEFT:
        case NoteBoundingBoxSide.RIGHT: {
          const m = textMeasurements[i];
          assertIsNotNullOrUndefined(m);
          const [, y] = targets[i];
          const labelMeasure = aabb2Height(m) / 2 + PADDING;
          const labelEnd = y - labelMeasure;

          if (currentEnd === undefined || labelEnd > currentEnd) {
            currentEnd = y;
          } else {
            currentEnd = currentEnd + labelMeasure;
          }

          textAlignments.push(TextAlignment.RIGHT);
          adjustedLabelPositions.push(currentEnd);

          break;
        }
      }
    });

    const bbHalfWidth = aabb2Width(primitivesOut.bb) / 2;

    tuples.forEach((tuple, i) => {
      const [, side, centroid] = tuple;
      const text = texts[i];
      const textAlign = textAlignments[i];

      const [x, y] = centroid;

      let labelX: number | undefined;
      let labelY: number | undefined;

      switch (side) {
        case NoteBoundingBoxSide.LEFT: {
          labelX = primitivesOut.xToPaperSpace(primitivesOut.dwgCenterX - PERIMETER_PADDING) - bbHalfWidth;
          labelY = primitivesOut.yToPaperSpace(adjustedLabelPositions[i]);
          break;
        }
        case NoteBoundingBoxSide.BOTTOM:
        case NoteBoundingBoxSide.TOP:
        case NoteBoundingBoxSide.RIGHT:
          break;
      }

      assertIsNumber(labelX);
      assertIsNumber(labelY);

      const elevNote = generateElevNoteWithLongLeaderSheetGeometry({
        x: labelX,
        y: labelY,
        text,
        alignment: textAlign,
        targetRelativeX: labelX - primitivesOut.xToPaperSpace(x),
        targetRelativeY: labelY - primitivesOut.yToPaperSpace(y),
      });

      for (const geom of elevNote) {
        primitivesOut.addPaperSpaceGeometry(geom, category, 10);
      }
    });
  }

  placeNotesOnPerimeter(leftNotes);
}

type PerimeterPosition = {
  side: Side;
  y: number;
  targetX: number;
  targetY: number;
};

type AssignedPerimeterPosition = PerimeterPosition & {
  cost: number;
  labelAABB: AABB2;
  leaderLine: PolyLine2;
};

type PerimeterPositionAssignment = (AssignedPerimeterPosition | null)[];

// Spacing between perimeter labels
const ENTITY_LEADER_INSET_DISTANCE = 1;

// Ideal angle range for the leader line to the target, based on height.
// Bottom of building uses a shallower angle, scaling up to the midpoint.
const IDEAL_LEADER_ANGLE_BOTTOM = degreeToRadian(15);
const IDEAL_LEADER_ANGLE_TOP = degreeToRadian(30);

const MIN_LEADER_OVERLAP_DISTANCE_SQUARED = 1;

// Various cost constants for the perimeter position cost function
const X_DISTANCE_COST_FACTOR = 1.0;
const Y_DISTANCE_COST_FACTOR = 1.0;
const ANGLE_COST_FACTOR = 50.0;
const ANGLE_COST_POWER = 1.5;

// Offset percentage of the home aabb height for the vertical scan
const OFFSET_PERCENTAGE = 0.02;
const TOP_OVERFLOW_ROW_COUNT = 4;

const MAX_ITERATIONS = 500;

const MIN_TARGET_POSITIONS = 10;

const NOTE_CATEGORY = SheetGeometryCategory.MATERIAL_TAGS;

interface PerimeterPositionCostParams {
  collisionSystem: CollisionSystem2;
  worldBB: AABB2;
  boxToElbowX: number;
  boxToLabelX: number;
  labelBoxer: LabelBoxer;
  primitivesOut: PlanGeometryBuilderWithCollider;
}

type PerimeterCosts = { position: PerimeterPosition; cost: number; tieBreaker: string };

type BestCost = {
  sumCost: number;
  leaderCost: number;
  overlapCost: number;
  position: PerimeterPosition;
  overlapEntities: number[];
};

type OverlappingEntitiesAndCosts = {
  overlapEntities: number[];
  overlapCost: number;
};

function computeLabelOrElbowX(side: Side, worldBB: AABB2, xSpacing: number): number {
  return side === Side.LEFT ? worldBB.minX - xSpacing : worldBB.maxX + xSpacing;
}

function pointLeaderLineDistanceSquared(p: Vec2, leaderLine: PolyLine2): number {
  assert(leaderLine.length === 6, "leaderLine must be a polyline with 6 points");
  const x1 = leaderLine[2];
  const y1 = leaderLine[3];
  const x2 = leaderLine[4];
  const y2 = leaderLine[5];
  const [x, y] = p;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  return vec2DistSquared(x, y, closestX, closestY);
}

function makeLabelGeometry(
  text: string,
  x: number,
  position: PerimeterPosition,
  textWidth: number,
  scaleFactor: number,
  sheetGeometryStyler?: SheetGeometryStyler,
): SheetTextGeometry {
  const { side, y } = position;
  const placementXAnchor = side === Side.LEFT ? PlacementXAnchor.RIGHT : PlacementXAnchor.LEFT;
  const geometry = sheetTextGeometryNew({
    x,
    y,
    text,
    placementXAnchor,
    boxWidth: sheetInchesNew(textWidth * scaleFactor),
    alignment: side === Side.LEFT ? TextAlignment.RIGHT : TextAlignment.LEFT,
    category: NOTE_CATEGORY,
  });
  if (sheetGeometryStyler) {
    return sheetGeometryStyler([geometry as TaggedPlanGeometry], NOTE_CATEGORY)[0] as SheetTextGeometry;
  } else {
    return geometry;
  }
}

type LabelBoxer = (key: number, position: PerimeterPosition) => AABB2;

function getPerimeterPositionKey(position: PerimeterPosition): string {
  return [position.side, position.y, position.targetX, position.targetY].join(":");
}

function getOverlappingEntitiesAndCosts(
  assignment: PerimeterPositionAssignment,
  entityKey: number,
  position: PerimeterPosition,
  worldBB: AABB2,
  boxToElbowX: number,
  boxToLabelX: number,
  labelBoxer: LabelBoxer,
): OverlappingEntitiesAndCosts {
  const overlapEntities: number[] = [];
  let overlapCost = 0.0;
  let labelAABB: AABB2 | undefined;
  let leaderLine: PolyLine2 | undefined;
  for (let otherKey = 0; otherKey < assignment.length; otherKey++) {
    const otherPosition = assignment[otherKey];
    // Skip if this entity is not assigned to a position.
    if (otherPosition === null) {
      continue;
    }
    // We assume we can't overlap with an opposite side leader/label.
    if (otherPosition.side !== position.side) {
      continue;
    }

    // Check if the two labels overlap.
    labelAABB ??= labelBoxer(entityKey, position);

    if (aabb2Overlaps(labelAABB, otherPosition.labelAABB)) {
      overlapCost += otherPosition.cost;
      overlapEntities.push(otherKey);
      continue;
    }

    // No label overlap. Next check if the two leader lines intersect.
    leaderLine ??= getLeaderPolyLine(position, worldBB, boxToElbowX, boxToLabelX);
    const intersection = intersectPolyLine2PolyLine2(leaderLine, otherPosition.leaderLine);
    if (intersection.length > 0) {
      overlapCost += otherPosition.cost;
      overlapEntities.push(otherKey);
      continue;
    }

    // No intersection. Next we check for leader line proximity.
    const target1 = [position.targetX, position.targetY];
    const target2 = [otherPosition.targetX, otherPosition.targetY];

    const distanceToLeaderLine1 = pointLeaderLineDistanceSquared(target1, otherPosition.leaderLine);
    const distanceToLeaderLine2 = pointLeaderLineDistanceSquared(target2, leaderLine);

    const minDistanceSquared = Math.min(distanceToLeaderLine1, distanceToLeaderLine2);
    if (minDistanceSquared < MIN_LEADER_OVERLAP_DISTANCE_SQUARED) {
      overlapCost += otherPosition.cost;
      overlapEntities.push(otherKey);
    }
  }

  return { overlapEntities, overlapCost };
}

function getLeaderPolyLine(
  position: PerimeterPosition,
  worldBB: AABB2,
  boxToElbowX: number,
  boxToLabelX: number,
): PolyLine2 {
  const { side, y, targetX, targetY } = position;
  const labelX = computeLabelOrElbowX(side, worldBB, boxToLabelX);
  const elbowX = computeLabelOrElbowX(side, worldBB, boxToElbowX);
  return polyLine2New([labelX, y, elbowX, y, targetX, targetY]);
}

// Using a staggered grid allows us to search more space while using fewer points.
function generateStaggeredGrid(aabb: AABB2, spacingX: number, spacingY: number): Vec2[] {
  const { minX, minY, maxX, maxY } = aabb;
  const height = maxY - minY;
  const width = maxX - minX;
  const points: Vec2[] = [];
  const offset = spacingX / 2;

  const rows = Math.ceil(height / spacingY);
  const cols = Math.ceil(width / spacingX);

  // Offset by half a row and half a column, or half the aabb width and height, whichever is smaller
  const offsetX = Math.min(spacingX / 2, width / 2);
  const offsetY = Math.min(spacingY / 2, height / 2);

  for (let row = 0; row < rows; row++) {
    const isEvenRow = row % 2 === 0;
    const xOffset = isEvenRow ? 0 : offset;

    for (let col = 0; col < cols; col++) {
      // Offset by half a row and half a column
      const x = minX + col * spacingX + xOffset + offsetX;
      const y = minY + row * spacingY + offsetY;

      if (aabb2ContainsVec2(aabb, x, y)) {
        points.push([x, y]);
      }
    }
  }

  return points;
}

enum Side {
  LEFT = 0,
  RIGHT = 1,
}

function getSideText(side: Side): string {
  return side === Side.LEFT ? "LEFT" : "RIGHT";
}

const STAGGERED_GRID_SPACING_X = 8;
const STAGGERED_GRID_SPACING_Y = 2;
const PERIMETER_TARGET_SPACING = 3;
const PERIMETER_TARGET_SPACING_SQUARED = PERIMETER_TARGET_SPACING * PERIMETER_TARGET_SPACING;

function getPerimeterInteriorTargetPositions(polys: PolyLine2[][], aabbCenterX: number): Vec2[][] {
  const targetPositions: Vec2[][] = init2DArray<Vec2>(2, 0);
  for (const offsetPolys of polys) {
    const offsetPoly = offsetPolys[0];
    const bb = polyLine2AABB2(offsetPoly);
    assertIsNotNullOrUndefined(bb);
    const gridPoints = generateStaggeredGrid(bb, STAGGERED_GRID_SPACING_X, STAGGERED_GRID_SPACING_Y);
    for (const point of gridPoints) {
      // reject if point is not contained in the offsetPoly
      if (classifyContainmentVec2PolyLine2(point[0], point[1], offsetPoly) !== Vec2PolyLine2ContainmentClass.INSIDE) {
        continue;
      }

      // Determine if the target is in  the left or right half of the world AABB
      const targetSide = point[0] < aabbCenterX ? Side.LEFT : Side.RIGHT;
      targetPositions[targetSide].push(point);
    }
  }

  return targetPositions;
}

// Filters out edges that have a normal that point down.
function addPerimeterEdgeTargetPositions(polys: PolyLine2[], aabbCenterX: number, targetPositions: Vec2[][]): void {
  function addTargetPositionIfFarEnough(side: Side, x: number, y: number): void {
    for (const [targetX, targetY] of targetPositions[side]) {
      if (vec2DistSquared(x, y, targetX, targetY) < PERIMETER_TARGET_SPACING_SQUARED) {
        return;
      }
    }
    targetPositions[side].push([x, y]);
  }

  for (const poly of polys) {
    for (let edgeIndex = 0, l = polyLine2EdgeCount(poly); edgeIndex < l; edgeIndex++) {
      const [, ny] = polyLine2EdgeNormal(poly, edgeIndex, true);
      if (ny < -DIST_EPSILON) {
        continue;
      }
      const edgeLength = polyLine2EdgeLength(poly, edgeIndex);

      const stepSize = 4;

      const startOffset = Math.min(stepSize / 2, edgeLength / 2);

      // sample points along the edge, stepping by 4 units
      for (let t = startOffset; t < edgeLength; t += stepSize) {
        const [x, y] = polyLine2EdgeEval(poly, edgeIndex, t, false);
        if (x < aabbCenterX) {
          addTargetPositionIfFarEnough(Side.LEFT, x, y);
        } else {
          addTargetPositionIfFarEnough(Side.RIGHT, x, y);
        }
      }
    }
  }
}

/** Callback for reporting errors during annotation placement. */
export type AnnotationPlacementErrorCallback = (error: Error) => void;

type PlaceEntityAnnotationsOnBoxPerimeterParams = {
  autoNotes: AutoEntityAnnotationInfo[];
  collisionSystem: CollisionSystem2;
  primitivesOut: PlanGeometryBuilderWithCollider;
  showElevationNoteEntities: ElevationNoteEntityDebugMode;
  elevationAnnotationParams?: ElevationAnnotationParams;
  sheetGeometryStyler?: SheetGeometryStyler;
  textScalingFactor?: number;
  onError?: AnnotationPlacementErrorCallback;
};

function renderFailedBoundingBoxPlacementDebugGeometry({
  entityKey,
  autoNotes,
  entityTargets,
  homeYValues,
  boxToElbowX,
  boxToLabelX,
  labelBoxer,
  collisionSystem,
  primitivesOut,
}: {
  entityKey: number;
  autoNotes: readonly AutoEntityAnnotationInfo[];
  entityTargets: Vec2[][][];
  homeYValues: readonly number[];
  boxToElbowX: number;
  boxToLabelX: number;
  labelBoxer: LabelBoxer;
  collisionSystem: CollisionSystem2;
  primitivesOut: PlanGeometryBuilderWithCollider;
}): void {
  const failedEntityPaths = autoNotes.filter((note) => note.key === entityKey).map((note) => polyLine2Copy(note.poly));
  const targetMarkerPaths: PolyLine2[] = [];
  const collidingLabelPaths: PolyLine2[] = [];
  const collidingLeaderPaths: PolyLine2[] = [];
  const blockerPaths: PolyLine2[] = [];
  const blockerPolylinePaths: PolyLine2[] = [];
  const blockerIndices = new Set<number>();

  for (const side of [Side.LEFT, Side.RIGHT]) {
    for (const [targetX, targetY] of entityTargets[entityKey][side]) {
      targetMarkerPaths.push(rect2ByCenterWidthHeight(targetX, targetY, 1, 1));

      for (const homeY of homeYValues) {
        if (homeY < targetY) {
          continue;
        }

        const position: PerimeterPosition = { side, y: homeY, targetX, targetY };
        const labelAABB = labelBoxer(entityKey, position);
        const labelBlockerIndices = new Set(collisionSystem.search(labelAABB));
        if (labelBlockerIndices.size > 0) {
          collidingLabelPaths.push(polyLine2FromAABB2(labelAABB));
          addBlockerPaths(labelBlockerIndices);
        }

        const leaderPath = getLeaderPolyLine(position, primitivesOut.worldBB, boxToElbowX, boxToLabelX);
        const paperSpaceLeaderPath = polyLine2TransformCopy(leaderPath, primitivesOut.modelSpaceToPaperSpaceTransform);
        const leaderBlockerIndices = new Set(collisionSystem.searchLeaderLine(paperSpaceLeaderPath));
        if (leaderBlockerIndices.size > 0) {
          collidingLeaderPaths.push(paperSpaceLeaderPath);
          addBlockerPaths(leaderBlockerIndices);
        }
      }
    }
  }

  function addBlockerPaths(indices: ReadonlySet<number>): void {
    for (const blocker of collisionSystem.all()) {
      if (!indices.has(blocker.index) || blockerIndices.has(blocker.index)) {
        continue;
      }
      blockerIndices.add(blocker.index);
      if (blocker.polyline) {
        blockerPolylinePaths.push(polyLine2Copy(blocker.polyline));
      } else {
        blockerPaths.push(polyLine2FromAABB2(blocker));
      }
    }
  }

  if (failedEntityPaths.length > 0) {
    primitivesOut.addWorldSpaceGeometry(
      sheetPolylineGeometryNew({
        paths: failedEntityPaths,
        fill: true,
        fillColor: "cyan",
        fillOpacity: percentNew(0.75),
        strokeColor: "magenta",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(1.2),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }

  if (targetMarkerPaths.length > 0) {
    primitivesOut.addWorldSpaceGeometry(
      sheetPolylineGeometryNew({
        paths: targetMarkerPaths,
        fill: true,
        fillColor: "yellow",
        fillOpacity: percentNew(0.8),
        strokeColor: "red",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(0.6),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }

  if (collidingLabelPaths.length > 0) {
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: collidingLabelPaths,
        fill: true,
        fillColor: "red",
        fillOpacity: percentNew(0.35),
        strokeColor: "red",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(0.6),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }

  if (collidingLeaderPaths.length > 0) {
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: collidingLeaderPaths,
        strokeColor: "orange",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(0.8),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }

  if (blockerPaths.length > 0) {
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: blockerPaths,
        fill: true,
        fillColor: "purple",
        fillOpacity: percentNew(0.35),
        strokeColor: "purple",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(0.6),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }
  if (blockerPolylinePaths.length > 0) {
    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: blockerPolylinePaths,
        strokeColor: "purple",
        strokeOpacity: percentNew(1),
        linewidth: sheetPointsNew(0.8),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
    );
  }
}

export function serializeBoundingBoxPlacementRepro({
  autoNotes,
  collisionSystem,
  primitivesOut,
  showElevationNoteEntities,
  elevationAnnotationParams = {},
  sheetGeometryStyler,
  textScalingFactor = 1,
  errorMessage,
}: Omit<PlaceEntityAnnotationsOnBoxPerimeterParams, "onError"> & {
  errorMessage?: string;
}): BoundingBoxPlacementReproData {
  const { boundingBoxElbowXSpacing, boundingBoxLabelXSpacing, labelTextWidth, levelAnnotationMethod } =
    elevationAnnotationParams;
  const { boxToElbowX, boxToLabelX, textWidth } = getBoundingBoxLabelSpacing(
    primitivesOut,
    boundingBoxElbowXSpacing,
    boundingBoxLabelXSpacing,
    labelTextWidth,
  );

  const maxKey = autoNotes.length > 0 ? Math.max(...autoNotes.map((entity) => ensureDefined(entity.key))) : -1;
  const styledLabels: BoundingBoxPlacementReproStyledLabel[] = [];

  for (const side of [Side.LEFT, Side.RIGHT]) {
    for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
      const text = autoNotes.find((entity) => entity.key === entityKey)?.text ?? "";
      const styledLabel = sheetGeometryStylePass(
        makeLabelGeometry(
          text,
          0,
          { side, y: 0, targetX: 0, targetY: 0 },
          textWidth,
          primitivesOut.scaleFactor,
          sheetGeometryStyler,
        ),
        undefined,
        textScalingFactor,
      );

      styledLabels.push({
        key: entityKey,
        side: getSideText(side) as "LEFT" | "RIGHT",
        text,
        fontSize: styledLabel.fontSize,
        font: styledLabel.font,
        boxWidth: styledLabel.boxWidth,
        alignment: styledLabel.alignment,
        placementXAnchor: styledLabel.placementXAnchor,
        rotation: styledLabel.rotation,
      });
    }
  }

  return {
    worldBB: aabb2Copy(primitivesOut.worldBB),
    scaleFactor: primitivesOut.scaleFactor,
    textScalingFactor,
    showElevationNoteEntities,
    elevationAnnotationParams: {
      levelAnnotationMethod,
      boundingBoxElbowXSpacing,
      boundingBoxLabelXSpacing,
      labelTextWidth,
    },
    computedSpacing: { boxToElbowX, boxToLabelX, textWidth },
    autoNotes: autoNotes.map((note) => ({
      key: ensureDefined(note.key),
      text: note.text,
      poly: [...note.poly],
    })),
    collisionItems: collisionSystem.all().map((item) => ({
      minX: item.minX,
      minY: item.minY,
      maxX: item.maxX,
      maxY: item.maxY,
      weight: item.weight,
      blocksLeaderLines: item.blocksLeaderLines,
      shapeType: item.shapeType,
      polyline: item.polyline ? [...item.polyline] : undefined,
    })),
    styledLabels,
    errorMessage,
  };
}

export async function placeEntityAnnotationsOnBoxPerimeter({
  autoNotes,
  collisionSystem,
  primitivesOut,
  showElevationNoteEntities,
  elevationAnnotationParams = {},
  sheetGeometryStyler,
  textScalingFactor = 1,
  onError,
}: PlaceEntityAnnotationsOnBoxPerimeterParams): Promise<void> {
  console.time("placeEntityAnnotationsOnBoxPerimeter");

  // Handle empty autoNotes case - nothing to annotate
  if (autoNotes.length === 0) {
    console.timeEnd("placeEntityAnnotationsOnBoxPerimeter");
    return;
  }

  const { worldBB } = primitivesOut;

  const maxKey = Math.max(...autoNotes.map((entity) => ensureDefined(entity.key)));
  // sanity check
  assert(maxKey >= 0 && maxKey < autoNotes.length, "maxKey is not within the range of autoNotes");

  function makeSideArray(): Vec2[][] {
    return init2DArray<Vec2>(2, 0);
  }

  // entityTargets[entityKey][side] = [[targetX1, targetY1], [targetX2, targetY2], ...]
  // EntityKey is between 0 and maxKey, so we can store the targets in an array
  const entityTargets: Vec2[][][] = new Array(maxKey + 1).fill(null).map(() => makeSideArray());

  const aabbCenterX = (worldBB.minX + worldBB.maxX) / 2;

  for (const entity of autoNotes) {
    const { poly, key } = entity;
    assertIsNotNullOrUndefined(key);

    try {
      const simplifiedPoly = polyLine2Simplify(poly);
      const polys = offsetClosedPolyLine2([simplifiedPoly], -ENTITY_LEADER_INSET_DISTANCE);

      const targetPositions = getPerimeterInteriorTargetPositions(polys, aabbCenterX);
      for (const side of [Side.LEFT, Side.RIGHT]) {
        entityTargets[key][side].push(...targetPositions[side]);
      }

      if (showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
        for (const offsetPolys of polys) {
          const offsetPoly = offsetPolys[0];
          const bb = polyLine2AABB2(offsetPoly);
          assertIsNotNullOrUndefined(bb);
          // draw bb
          const bbGeometry = sheetPolylineGeometryNew({
            paths: [offsetPoly],
            strokeColor: "blue",
            strokeOpacity: percentNew(1),
            linewidth: sheetPointsNew(0.4),
          });
          primitivesOut.addWorldSpaceGeometry(bbGeometry, SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES);
        }
      }
    } catch (error) {
      const errorMessage = `offsetClosedPolyLine2 failed for entity ${getEntityText(key)}. ${
        error.message
      }. Please contact support.`;
      onError?.(makeError(errorMessage));
      const targetPositions = getPerimeterInteriorTargetPositions([[poly]], aabbCenterX);
      for (const side of [Side.LEFT, Side.RIGHT]) {
        entityTargets[key][side].push(...targetPositions[side]);
      }
    }
  }

  // For entities that lack enough target positions, add more target positions by sampling the edges of the polys
  for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
    const targetPositions = entityTargets[entityKey];
    const totalTargetPositions = targetPositions[Side.LEFT].length + targetPositions[Side.RIGHT].length;
    if (totalTargetPositions < MIN_TARGET_POSITIONS) {
      const candidates = autoNotes.filter((entity) => ensureDefined(entity.key) === entityKey);
      const polys = candidates.map((entity) => entity.poly);
      addPerimeterEdgeTargetPositions(polys, aabbCenterX, targetPositions);
    }
  }

  if (showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
    for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
      const targetPositions = entityTargets[entityKey];
      for (const side of [Side.LEFT, Side.RIGHT]) {
        const fillColor = side === Side.LEFT ? "red" : "blue";
        for (const targetPosition of targetPositions[side]) {
          const [targetX, targetY] = targetPosition;
          const rect = rect2ByCenterWidthHeight(targetX, targetY, 0.5, 0.5);
          const labelGeometry = sheetPolylineGeometryNew({
            paths: [rect],
            fill: true,
            fillColor,
            strokeOpacity: percentNew(0),
          });
          primitivesOut.addWorldSpaceGeometry(labelGeometry, SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES);
        }
      }
    }
  }

  const { boundingBoxElbowXSpacing, boundingBoxLabelXSpacing, labelTextWidth } = elevationAnnotationParams;
  const { boxToElbowX, boxToLabelX, textWidth } = getBoundingBoxLabelSpacing(
    primitivesOut,
    boundingBoxElbowXSpacing,
    boundingBoxLabelXSpacing,
    labelTextWidth,
  );

  // planTextGeometryAABB is slow, so cache AABBs for the labels at position (0, 0).
  const labelAABBs: AABB2[][] = [];
  for (const side of [Side.LEFT, Side.RIGHT]) {
    const sideAABBs: AABB2[] = [];
    for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
      const worldSpace = makeLabelGeometry(
        getEntityText(entityKey),
        0,
        { side, y: 0, targetX: 0, targetY: 0 },
        textWidth,
        primitivesOut.scaleFactor,
        sheetGeometryStyler,
      );

      const paperSpace = primitivesOut.toPaperSpaceGeometry(worldSpace) as SheetTextGeometry;
      const scaledPaperSpace = sheetGeometryStylePass(paperSpace, undefined, textScalingFactor);
      sideAABBs.push(planTextGeometryAABB(scaledPaperSpace));
    }
    labelAABBs.push(sideAABBs);
  }

  // Boxer function that moves the cached label AABB to the position (labelX, position.y)
  const labelBoxer: LabelBoxer = (key: number, position: PerimeterPosition) => {
    const labelX = computeLabelOrElbowX(position.side, worldBB, boxToLabelX);
    const labelY = position.y;

    const paperX = labelX * primitivesOut.scaleFactor;
    const paperY = labelY * -1 * primitivesOut.scaleFactor;

    const labelAABB = labelAABBs[position.side][key];
    assertIsNotNullOrUndefined(labelAABB);
    const movedAABB = aabb2Move(aabb2Copy(labelAABB), paperX, paperY);

    return movedAABB;
  };

  const params: PerimeterPositionCostParams = {
    collisionSystem,
    worldBB,
    boxToElbowX,
    boxToLabelX,
    labelBoxer,
    primitivesOut,
  };

  const costMatrix: PerimeterCosts[][] = new Array(maxKey + 1).fill(null).map(() => []);

  // produce possible home y values by scanning the world BB and adding every % of the height
  const homeYValues: number[] = [];
  const homeHeight = aabb2Height(params.worldBB);
  const homeHeightFraction = homeHeight * OFFSET_PERCENTAGE;
  const offsetCount = Math.floor(1 / OFFSET_PERCENTAGE);
  for (let offsetN = 0; offsetN <= offsetCount; offsetN++) {
    homeYValues.push(params.worldBB.minY + homeHeightFraction * offsetN);
  }

  if (showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
    for (const homeY of homeYValues) {
      for (const side of [Side.LEFT, Side.RIGHT]) {
        const labelX = computeLabelOrElbowX(side, worldBB, boxToLabelX);
        const aabb = rect2ByCenterWidthHeight(labelX, homeY, 1.0, 1.0);
        const pointGeometry = sheetPolylineGeometryNew({
          paths: [aabb],
          fill: true,
          fillColor: "green",
          strokeOpacity: percentNew(0),
        });
        primitivesOut.addWorldSpaceGeometry(pointGeometry, SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES);
      }
    }
  }

  // Build the cost matrix for each combination: target position * note y value * side
  const ROUND_FACTOR = 100;
  function roundCost(cost: number): number {
    return Math.round(cost * ROUND_FACTOR) / ROUND_FACTOR;
  }

  /**
   * Computes the ideal leader angle based on the target's relative height within the building.
   * Returns a shallower angle (15 degrees) at the bottom, scaling linearly to 30 degrees at the midpoint,
   * and clamping at 30 degrees for the upper half.
   */
  function getIdealLeaderAngle(targetY: number, worldBB: AABB2): number {
    const buildingHeight = aabb2Height(worldBB);
    const halfHeight = buildingHeight / 2;

    // Calculate relative position from bottom (0) to middle (1), clamped
    const relativeHeight = Math.min(1, Math.max(0, (targetY - worldBB.minY) / halfHeight));

    // Linearly interpolate between bottom angle and top angle
    return IDEAL_LEADER_ANGLE_BOTTOM + relativeHeight * (IDEAL_LEADER_ANGLE_TOP - IDEAL_LEADER_ANGLE_BOTTOM);
  }

  function calculatePerimeterLeaderLineCost(
    entityKey: number,
    position: PerimeterPosition,
    { collisionSystem, worldBB, boxToElbowX, boxToLabelX, labelBoxer, primitivesOut }: PerimeterPositionCostParams,
  ): number {
    const { side, y, targetX, targetY } = position;

    // Reject if below the target y
    const distY = y - targetY;
    if (distY < 0) {
      return Infinity;
    }

    // Check for collision and give it a huge cost
    const labelAABB = labelBoxer(entityKey, position);
    if (collisionSystem.collides(labelAABB)) {
      return Infinity;
    }

    const leaderLine = getLeaderPolyLine(position, worldBB, boxToElbowX, boxToLabelX);
    const paperSpaceLeaderLine = polyLine2TransformCopy(leaderLine, primitivesOut.modelSpaceToPaperSpaceTransform);
    if (collisionSystem.collidesLeaderLine(paperSpaceLeaderLine)) {
      return Infinity;
    }

    // Penalize angles that deviate from the ideal angle (which varies by height)
    const elbowX = computeLabelOrElbowX(side, worldBB, boxToElbowX);
    const distX = targetX - elbowX;
    const angle = side === Side.LEFT ? Math.atan2(distY, distX) : Math.PI - Math.atan2(distY, distX);
    const angleNormalized = angleClean(angle, Math.PI / 2);
    const idealAngle = getIdealLeaderAngle(targetY, worldBB);
    const angleDiff = Math.abs(idealAngle - angleNormalized);

    let cost = 0;
    cost += Math.abs(distX) * X_DISTANCE_COST_FACTOR;
    cost += Math.abs(distY) * Y_DISTANCE_COST_FACTOR;

    // Instead of a linear cost, use a quadratic cost to penalize angles close to 0 or 90 degrees,
    // while permitting variation around 45 degrees.
    cost += Math.pow(angleDiff * ANGLE_COST_FACTOR, ANGLE_COST_POWER);

    return roundCost(cost);
  }

  function makeError(message: string): Error {
    return new ElevationAnnotationPlacementError(`elevation view note placement failed: ${message}`);
  }

  function addFiniteRowCostsForYValues(
    rowCosts: PerimeterCosts[],
    entityKey: number,
    yValues: readonly number[],
  ): void {
    for (const side of [Side.LEFT, Side.RIGHT]) {
      const targetPositions = entityTargets[entityKey][side];
      for (const targetPosition of targetPositions) {
        const [targetX, targetY] = targetPosition;
        for (const homeY of yValues) {
          const position: PerimeterPosition = { side, y: homeY, targetX, targetY };
          const cost = calculatePerimeterLeaderLineCost(entityKey, position, params);
          if (isFinite(cost)) {
            const tieBreaker = getPerimeterPositionKey(position);
            rowCosts.push({ position, cost, tieBreaker });
          }
        }
      }
    }
  }

  function getTopOverflowYValues(entityKey: number): number[] {
    const targetPositions = [...entityTargets[entityKey][Side.LEFT], ...entityTargets[entityKey][Side.RIGHT]];
    const hasTopEdgeTarget = targetPositions.some(([, targetY]) => targetY >= worldBB.maxY);
    const entityTouchesTopEdge = autoNotes.some((entity) => {
      if (entity.key !== entityKey) {
        return false;
      }
      const aabb = polyLine2AABB2(entity.poly);
      return aabb !== undefined && aabb.maxY >= worldBB.maxY - DIST_EPSILON;
    });
    if (!hasTopEdgeTarget && !entityTouchesTopEdge) {
      return [];
    }

    const overflowYValues: number[] = [];
    for (let rowIndex = 1; rowIndex <= TOP_OVERFLOW_ROW_COUNT; rowIndex++) {
      overflowYValues.push(worldBB.maxY + homeHeightFraction * rowIndex);
    }
    return overflowYValues;
  }

  for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
    const rowCosts: PerimeterCosts[] = [];

    addFiniteRowCostsForYValues(rowCosts, entityKey, homeYValues);
    if (rowCosts.length === 0) {
      addFiniteRowCostsForYValues(rowCosts, entityKey, getTopOverflowYValues(entityKey));
    }

    if (rowCosts.length === 0) {
      if (showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
        renderFailedBoundingBoxPlacementDebugGeometry({
          entityKey,
          autoNotes,
          entityTargets,
          homeYValues,
          boxToElbowX,
          boxToLabelX,
          labelBoxer,
          collisionSystem,
          primitivesOut,
        });
      }
      onError?.(makeError(`No valid positions for entity "${getEntityText(entityKey)}".`));
      return;
    }

    rowCosts.sort((a, b) => {
      if (a.cost !== b.cost) {
        return a.cost - b.cost;
      }
      return a.tieBreaker.localeCompare(b.tieBreaker);
    });
    costMatrix[entityKey] = rowCosts;
  }

  const epsilon = 1;

  const assignment: PerimeterPositionAssignment = new Array(maxKey + 1).fill(null);

  function getUnassignedEntityKey(): number | undefined {
    for (let entityKey = 0; entityKey <= maxKey; entityKey++) {
      if (assignment[entityKey] === null) {
        return entityKey;
      }
    }
    return undefined;
  }

  function getEntityText(entityKey: number): string {
    return autoNotes.find((entity) => entity.key === entityKey)?.text ?? "";
  }

  let iterations = 0;

  // Note that we can fail to find a solution in MAX_ITERATIONS, if we try to stack too many annotations in the
  // drawing. We can improve this by allowing overlap, searching more y positions, or allowing annotations to
  // stack higher than the height of the drawing's bounding box.
  while (iterations < MAX_ITERATIONS) {
    const entityKey = getUnassignedEntityKey();
    if (entityKey === undefined) {
      break;
    }
    const entityCosts = costMatrix[entityKey];
    assert(entityCosts.length > 0, `entityCosts is empty for entity ${getEntityText(entityKey)}`);
    let bestCost: BestCost | undefined;
    let secondBestCost: BestCost | undefined;
    for (const entityCost of entityCosts) {
      const leaderCost = entityCost.cost;
      const { overlapEntities, overlapCost } = getOverlappingEntitiesAndCosts(
        assignment,
        entityKey,
        entityCost.position,
        worldBB,
        boxToElbowX,
        boxToLabelX,
        labelBoxer,
      );
      const newCost: BestCost = {
        sumCost: leaderCost + overlapCost,
        leaderCost,
        position: entityCost.position,
        overlapEntities,
        overlapCost,
      };

      if (bestCost === undefined || newCost.sumCost < bestCost.sumCost) {
        secondBestCost = bestCost;
        bestCost = newCost;
      } else if (secondBestCost === undefined || newCost.sumCost < secondBestCost.sumCost) {
        secondBestCost = newCost;
      }
    }

    assertIsNotNullOrUndefined(bestCost);
    if (secondBestCost === undefined) {
      secondBestCost = { ...bestCost, sumCost: bestCost.sumCost + epsilon };
    }

    const costDifferenceToSecondBest = secondBestCost.sumCost - bestCost.sumCost;
    const overlapCostForBest = bestCost.overlapCost;
    const newCost = Math.ceil(overlapCostForBest + Math.max(costDifferenceToSecondBest * 0.95, epsilon));

    assignment[entityKey] = {
      ...bestCost.position,
      cost: newCost,
      labelAABB: labelBoxer(entityKey, bestCost.position),
      leaderLine: getLeaderPolyLine(bestCost.position, worldBB, boxToElbowX, boxToLabelX),
    };

    // For debugging, add a thin outline of the plan text geometry aabb to the primitivesOut
    // const aabbGeometry = sheetPolylineGeometryNew({
    //   paths: [polyLine2FromAABB2(labelBoxer(entityKey, bestCost.position))],
    //   strokeColor: "red",
    //   strokeOpacity: percentNew(1),
    //   linewidth: sheetPointsNew(0.1),
    //   fill: false,
    // });
    // primitivesOut.addPaperSpaceDebugGeometry(aabbGeometry, SheetGeometryCategory.MATERIAL_TAGS);

    if (ASSIGNMENT_DEBUG_LOGGING) {
      console.log(
        "assigned entity",
        getEntityText(entityKey),
        "to",
        getSideText(bestCost.position.side),
        bestCost.position.y,
        "at cost",
        bestCost,
        "new cost",
        newCost,
        "second best cost",
        secondBestCost,
      );
    }

    for (const overlapEntityKey of bestCost.overlapEntities) {
      assertIsNotNullOrUndefined(assignment[overlapEntityKey]);

      if (ASSIGNMENT_DEBUG_LOGGING) {
        console.log("unassigned entity", overlapEntityKey, getEntityText(overlapEntityKey));
      }
      assignment[overlapEntityKey] = null;
    }
    iterations++;
  }

  if (assignment.some((entityAssignment) => entityAssignment === null)) {
    const unassignedCount = assignment.filter((entityAssignment) => entityAssignment === null).length;
    const errorMessage = `${unassignedCount} entities could not be assigned positions. Please adjust your settings or use another placement type.`;
    onError?.(makeError(errorMessage));
  }

  // Generate the geometry for the assigned positions
  for (const [entityKey, entityAssignment] of assignment.entries()) {
    if (entityAssignment === null) {
      continue;
    }
    // Add leader polyline
    const leaderPath = getLeaderPolyLine(entityAssignment, worldBB, boxToElbowX, boxToLabelX);
    const leaderGeometry = sheetPolylineGeometryNew({ paths: [leaderPath] });
    primitivesOut.addWorldSpaceGeometry(leaderGeometry, NOTE_CATEGORY);

    // Add arrowhead geometry
    const [labelX, , elbowX, elbowY, targetX, targetY] = leaderPath;
    const vector = vec2Sub(targetX, targetY, elbowX, elbowY);
    const rotationRads = vec2AngleBetweenCW(vector[0], vector[1], 0, 1);
    const arrowheadGeometry = generateArrowheadGeometry({
      position: [targetX, targetY],
      rotationRads,
      width: 3,
      height: 4,
    });
    primitivesOut.addWorldSpaceGeometry(arrowheadGeometry, NOTE_CATEGORY);

    // Add label text
    const labelGeometry = makeLabelGeometry(
      getEntityText(entityKey),
      labelX,
      entityAssignment,
      textWidth,
      primitivesOut.scaleFactor,
    );

    primitivesOut.addWorldSpaceGeometry(labelGeometry, NOTE_CATEGORY);

    if (showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
      // use labelboxer to get the aabb
      const labelAABB = labelBoxer(entityKey, entityAssignment);
      assertIsNotNullOrUndefined(labelAABB);
      const pl = polyLine2FromAABB2(labelAABB);
      primitivesOut.addPaperSpaceDebugGeometry(
        sheetPolylineGeometryNew({
          paths: [pl],
          fill: true,
          fillOpacity: percentNew(0.5),
          fillColor: "green",
          strokeOpacity: percentNew(1),
          linewidth: sheetPointsNew(0.5),
        }),
        SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
      );
    }
  }
  console.timeEnd("placeEntityAnnotationsOnBoxPerimeter");
}

export function placeEntityAnnotationsInPlace(
  entityAnnotationInfo: AutoEntityAnnotationInfo[],
  collisionSystem: CollisionSystem2,
  category: SheetGeometryCategory,
  primitivesOut: PlanGeometryBuilderWithCollider,
  allowBelowGroundAnnotations: boolean,
  groundZ: number,
  textScalingFactor: number,
): void {
  entityAnnotationInfo.sort((a, b) => polyLine2Area(b.poly) - polyLine2Area(a.poly));

  for (const index of Object.keys(entityAnnotationInfo)) {
    const { text, poly } = entityAnnotationInfo[index];

    let lowestWeightEdgeIndex: number | undefined;
    let lowestWeightParam: number | undefined;
    let lowestWeight = 99999999;
    let lowestWeightIsNormalized: boolean | undefined;

    // cycle through entity polyline edges and params to find the lowest weight position for entity notes
    for (let i = 0, l = polyLine2EdgeCount(poly); i < l; i++) {
      const [nx, ny] = polyLine2EdgeNormal(poly, i, true);
      const edgeLength = polyLine2EdgeLength(poly, i);
      const isLongEdge = edgeLength > 20;

      const start = isLongEdge ? 0.95 : edgeLength;
      const end = isLongEdge ? 0.05 : 0;
      const step = isLongEdge ? 0.05 : Math.max(2, edgeLength / 20);

      for (let j = start; j > end; j -= step) {
        const [x, y] = polyLine2EdgeEval(poly, i, j, isLongEdge);
        if (y < groundZ && !allowBelowGroundAnnotations) {
          continue;
        }
        const [tx, ty] = primitivesOut.ptToPaperSpace(x, y);
        const elevNote = generateElevNoteSheetGeometries({ x: tx, y: ty, text, leaderVector: [nx, ny] });
        const categorizedElevNote = supplyGeometryListTagIfMissing(elevNote, SheetGeometryCategory.MATERIAL_TAGS);

        const favorCenterWeightForNonShortEdges = edgeLength < 0.3 ? 35 : Math.abs(j - 0.5) * 10;

        const weight =
          collisionSystem.scoreSheetGeometries(categorizedElevNote, textScalingFactor) +
          favorCenterWeightForNonShortEdges;

        if (weight < lowestWeight) {
          lowestWeightIsNormalized = isLongEdge;
          lowestWeight = weight;
          lowestWeightEdgeIndex = i;
          lowestWeightParam = j;
        }
      }
    }

    if (lowestWeightEdgeIndex === undefined || lowestWeightParam === undefined) {
      return;
    }

    // insert note with lowest weight into collision system
    const [x, y] = polyLine2EdgeEval(poly, lowestWeightEdgeIndex, lowestWeightParam, lowestWeightIsNormalized);
    const [nx, ny] = polyLine2EdgeNormal(poly, lowestWeightEdgeIndex, true);
    const [tx, ty] = primitivesOut.ptToPaperSpace(x, y);
    const elevNote = generateElevNoteSheetGeometries({ x: tx, y: ty, text, leaderVector: [nx, ny] });

    for (const geom of elevNote) {
      primitivesOut.addPaperSpaceGeometry(geom, category, 10);
    }
  }
}

interface BoundingBoxLabelSpacing {
  boxToElbowX: SheetInches;
  boxToLabelX: SheetInches;
  textWidth: SheetInches;
}

export function getBoundingBoxLabelSpacing(
  primitivesOut: PlanGeometryBuilderWithCollider,
  boundingBoxToElbowXSpacing?: SheetInches,
  elbowToLabelXSpacing?: SheetInches,
  labelTextWidth?: SheetInches,
): BoundingBoxLabelSpacing {
  function getSpacing(spacing: SheetInches | undefined, defaultSpacing: SheetInches): SheetInches {
    return numDiv(spacing !== undefined ? spacing : defaultSpacing, primitivesOut.scaleFactor);
  }

  const boxToElbowX = getSpacing(boundingBoxToElbowXSpacing, defaultElevationAnnotationParams.boundingBoxElbowXSpacing);
  const boxToLabelX = numAdd(
    boxToElbowX,
    getSpacing(elbowToLabelXSpacing, defaultElevationAnnotationParams.boundingBoxLabelXSpacing),
  );
  const textWidth = getSpacing(labelTextWidth, defaultElevationAnnotationParams.labelTextWidth);

  return { boxToElbowX, boxToLabelX, textWidth };
}

type FilterEntityCandidatesToAnnotateParams = {
  entityId: string;
  polylines: PolyLine2[];
  entityTable: ViewEntityTable;
  entityAnnotationInfos: AutoEntityAnnotationInfo[];
  selectedEntityAnnotations: EntitiesAnnotationTable;
  settings: ViewNoteSettings;
  minimumArea: number;
  minimumBBMinDimForTagging: number;
  minimumBBMaxDimForTagging: number;
  elevationNotePlacementType: ElevationNotePlacementType;
};

function filterEntityCandidatesToAnnotate({
  entityId,
  polylines,
  entityTable,
  entityAnnotationInfos,
  selectedEntityAnnotations,
  settings,
  minimumArea,
  minimumBBMinDimForTagging,
  minimumBBMaxDimForTagging,
  elevationNotePlacementType,
}: FilterEntityCandidatesToAnnotateParams): void {
  const bb = aabb2ByPolyLine2s(polylines);
  if (!bb) {
    return;
  }

  const bbWidth = aabb2Width(bb);
  const bbHeight = aabb2Height(bb);

  const entityDescriptor = entityTable.getEntityDescriptor(entityId);
  const { requirements, bypass } = getDescriptorEntityAnnotationSetting(entityDescriptor, settings);
  const entityType = entityDescriptor?.entityType ?? ViewEntityType.NONE;

  if (!requirements || !requirements.enabled) {
    return;
  }

  if (!bypass && !EntityAnnotationEnabledTypes[entityDescriptor?.entityType]?.enabled) {
    return;
  }

  // For BOUNDING_BOX placement, force tagOnlyOne=true to ensure deterministic hash keys
  // (otherwise the hash includes entityId which varies between builds)
  const tagOnlyOne =
    elevationNotePlacementType === ElevationNotePlacementType.BOUNDING_BOX ? true : requirements.tagOnlyOne;

  // Respect tagOnlyOne for typical suffix
  const typicalSuffix = requirements.tagOnlyOne ? settings.typicalSuffix : "";

  let totalArea = 0;
  const plAreaCache = new Array<number>(polylines.length);

  for (let index = 0; index < polylines.length; index++) {
    const p = polylines[index];

    const area = polyLine2Area(p);
    plAreaCache[index] = area;
    totalArea += area;
  }

  if (totalAreaByAABBViewEntityType[entityType]) {
    const aabb = aabb2ByPolyLine2s(polylines);
    if (aabb) {
      totalArea = aabb2Width(aabb) * aabb2Height(aabb);
    }
  }

  if (totalArea < minimumArea) {
    return;
  }

  const minDimension = Math.min(bbWidth, bbHeight);
  const maxDimension = Math.max(bbWidth, bbHeight);

  if (minDimension < minimumBBMinDimForTagging) {
    return;
  }

  if (maxDimension < minimumBBMaxDimForTagging) {
    return;
  }

  const d = entityDescriptor as ViewEntityDescriptor;

  function autoEntityAnnotationInfoNew(polylineIndex: number, text: string): AreaEntityAnnotationInfo {
    return { type: "AUTO", poly: polylines[polylineIndex], text, area: plAreaCache[polylineIndex] };
  }

  function hashNoteInfo(entityType: ViewEntityType, text = "", entityId?: string): number {
    return hashString(JSON.stringify({ entityType, text, entityId }));
  }

  function addEntityAnnotationInfosToTable({ hash, text }: { hash: number; text: string }) {
    const newEntityInfos = polylines.map((_, i) => autoEntityAnnotationInfoNew(i, text));

    if (!selectedEntityAnnotations[hash]) {
      selectedEntityAnnotations[hash] = newEntityInfos;
    } else {
      selectedEntityAnnotations[hash].push(...newEntityInfos);
    }
  }

  if (d.type === "VIEW_ENTITY_DESCRIPTOR") {
    if (d.viewNote?.text === undefined || d.viewNote.text === "") {
      return;
    }

    if (tagOnlyOne) {
      addEntityAnnotationInfosToTable({
        hash: hashNoteInfo(d.entityType, d.viewNote.text),
        text: d.viewNote.text + ` ${typicalSuffix}`,
      });
    } else {
      addEntityAnnotationInfosToTable({
        hash: hashNoteInfo(d.entityType, d.viewNote.text, d.entityId),
        text: d.viewNote.text,
      });
    }
  } else if (tagOnlyOne) {
    switch (entityDescriptor.entityType) {
      case ViewEntityType.PROFILE_EXTRUSION: {
        const profileDescriptor = entityDescriptor as ProfileExtrusionDescriptor;
        if (!profileDescriptor.elevationNoteEnabled) {
          return;
        }

        if (!profileDescriptor.elevationNote) {
          return;
        }

        addEntityAnnotationInfosToTable({
          hash: hashNoteInfo(profileDescriptor.entityType, profileDescriptor.elevationNote),
          text: profileDescriptor.elevationNote,
        });
        break;
      }

      default: {
        const typeNote = requirements.text;
        if (typeNote === undefined || typeNote === "") {
          return;
        }

        addEntityAnnotationInfosToTable({
          hash: hashNoteInfo(entityType),
          text: typeNote + ` ${typicalSuffix}`,
        });
        break;
      }
    }
  } else {
    const text = requirements.text;
    if (text === undefined || text === "") {
      return;
    }
    for (const poly of polylines) {
      entityAnnotationInfos.push({ type: "AUTO", poly, text });
    }
  }
}
